"use client";

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { usePromo } from './context/PromoContext';

// Separate Toast component to prevent PromoRibbon rerenders
const ToastNotification = ({ toast, onHide }) => {
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (toast?.visible) {
      timeoutRef.current = setTimeout(() => {
        onHide();
      }, 2500);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [toast?.visible, onHide]);

  return (
    <AnimatePresence>
      {toast?.visible && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          className="fixed top-4 right-4 z-[9999] bg-green-600 text-white px-5 py-3 rounded-lg shadow-xl flex items-center gap-3 pointer-events-auto"
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 9999
          }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="font-medium">{toast.message}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Memoized promo item component
const PromoItem = React.memo(({ promo, onPromoClick, isLast }) => {
  const promoData = typeof promo === 'string'
    ? { displayText: promo, code: null, product_id: null }
    : promo;

  const displayCode = promoData.code || promoData.displayText;

  const handleClick = useCallback(() => {
    onPromoClick(promoData);
  }, [promoData, onPromoClick]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  return (
    <>
      <span
        className="text-white text-sm md:text-xs sm:text-xs font-medium hover:text-[#FCBD01] transition-colors duration-200 cursor-pointer px-4 md:px-3 sm:px-2 select-none"
        role="button"
        tabIndex="0"
        aria-label={`Copy promo code: ${displayCode}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        title="Click to copy promo code"
      >
        {promoData.displayText} <span className="text-[#FCBD01] font-bold">COUPON</span>
      </span>
      {!isLast && (
        <div className="w-px h-4 md:h-3 sm:h-2 bg-white/70 mx-6 md:mx-4 sm:mx-3" aria-hidden="true" />
      )}
    </>
  );
});

PromoItem.displayName = 'PromoItem';

const PromoRibbon = () => {
  const [isClient, setIsClient] = useState(false);
  const [toast, setToast] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  const containerRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const animationRef = useRef(null);

  const { promoCodes, promoCodesLoading, hasPromoCodes, promoCodesError } = usePromo();

  // Ensure we're on the client side for portal
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Create extended promo codes with stable keys
  const extendedPromoCodes = useMemo(() => {
    if (!promoCodes.length) return [];

    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const estimatedItemWidth = 250;
    const originalWidth = promoCodes.length * estimatedItemWidth;
    const copiesNeeded = Math.max(3, Math.ceil((viewportWidth * 2) / originalWidth));

    const repeated = [];
    for (let i = 0; i < copiesNeeded; i++) {
      promoCodes.forEach((promo, index) => {
        repeated.push({
          ...promo,
          uniqueKey: `${promo.code || promo.displayText}-${i}-${index}`
        });
      });
    }

    return repeated;
  }, [promoCodes]);

  // Setup ResizeObserver for container width measurement
  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerWidth(rect.width);
      }
    };

    // Initial measurement
    updateWidth();

    // Setup ResizeObserver
    if (window.ResizeObserver) {
      resizeObserverRef.current = new ResizeObserver(updateWidth);
      resizeObserverRef.current.observe(containerRef.current);
    } else {
      // Fallback for older browsers
      window.addEventListener('resize', updateWidth);
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      } else {
        window.removeEventListener('resize', updateWidth);
      }
    };
  }, [extendedPromoCodes.length]);

  // Calculate animation duration based on content width
  const animationDuration = useMemo(() => {
    if (!containerWidth || !extendedPromoCodes.length) return 60;
    const itemWidth = 250; // estimated width per item
    const totalWidth = extendedPromoCodes.length * itemWidth;
    return Math.max(15, totalWidth / 100); // minimum 30s, otherwise based on content
    // return Math.max(30, totalWidth / 60); 
  }, [containerWidth, extendedPromoCodes.length]);

  // Copy to clipboard handler
  const handlePromoClick = useCallback(async (promo) => {
    const code = promo.code || promo.displayText?.match(/[A-Z0-9]{6,}/)?.[0] || promo.displayText;

    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      setToast({ message: `${code} copied!`, visible: true });
    } catch (err) {
      setToast({ message: "Failed to copy", visible: true });
    }
  }, []);

  // Toast hide handler
  const handleHideToast = useCallback(() => {
    setToast(prev => prev ? { ...prev, visible: false } : null);
  }, []);

  // Mouse event handlers
  const handleMouseEnter = useCallback(() => {
    setIsPaused(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPaused(false);
  }, []);

  if (promoCodesLoading) {
    return (
      <div className="relative w-full bg-[#076461] h-12 md:h-10 sm:h-8 border-b-2 border-[#FCBD01]/20 overflow-hidden">
        <div className="flex items-center justify-center h-full">
          <div className="text-white text-sm md:text-xs sm:text-xs font-medium animate-pulse">
            Loading offers...
          </div>
        </div>
      </div>
    );
  }

  if (promoCodesError || !hasPromoCodes) {
    return null;
  }

  return (
    <>
      {/* Toast Notification - Rendered in Portal */}
      {isClient && typeof document !== 'undefined' && createPortal(
        <ToastNotification toast={toast} onHide={handleHideToast} />,
        document.body
      )}

      {/* Main Ribbon */}
      <motion.section
        className="relative w-full bg-[#076461] h-12 md:h-10 sm:h-8 border-b-2 border-[#FCBD01]/20 overflow-hidden"
        aria-label="Promotional offers ribbon"
        role="banner"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {/* Decorative background patterns */}
        <div className="absolute inset-0 opacity-10" aria-hidden="true">
          <div className="absolute top-1 left-4 w-6 h-6 border border-white rounded-full max-md:w-4 max-md:h-4 max-sm:w-3 max-sm:h-3"></div>
          <div className="absolute top-2 right-8 w-4 h-4 border border-white rounded-full max-md:w-3 max-md:h-3 max-sm:w-2 max-sm:h-2"></div>
          <div className="absolute top-1 left-1/4 w-3 h-3 bg-white rounded-full max-md:w-2 max-md:h-2 max-sm:w-1 max-sm:h-1"></div>
          <div className="absolute top-2 right-1/3 w-8 h-8 border border-white rounded-full max-md:w-6 max-md:h-6 max-sm:w-4 max-sm:h-4"></div>
          <div className="absolute top-1 left-1/2 w-2 h-2 bg-white rounded-full max-sm:w-1 max-sm:h-1"></div>
          <div className="absolute top-2 right-1/4 w-3 h-3 border border-white transform rotate-45 max-md:w-2 max-md:h-2 max-sm:w-1 max-sm:h-1"></div>
          <div className="absolute top-1 left-3/4 w-5 h-5 border border-white transform rotate-45 max-md:w-3 max-md:h-3 max-sm:w-2 max-sm:h-2"></div>
        </div>

        {/* Left & Right edge decorations */}
        <div className="absolute left-0 top-0 h-full w-8 md:w-6 sm:w-4 flex items-center justify-center opacity-20" aria-hidden="true">
          <div className="flex items-center space-x-1 md:space-x-0.5 sm:space-x-0.5">
            <div className="w-2 h-2 md:w-1.5 md:h-1.5 sm:w-1 sm:h-1 border border-[#FCBD01] rounded-full"></div>
            <div className="w-1.5 h-1.5 md:w-1 md:h-1 sm:w-0.5 sm:h-0.5 bg-[#FCBD01] rounded-full"></div>
            <div className="w-2 h-2 md:w-1.5 md:h-1.5 sm:w-1 sm:h-1 border border-[#FCBD01] transform rotate-45"></div>
          </div>
        </div>

        <div className="absolute right-0 top-0 h-full w-8 md:w-6 sm:w-4 flex items-center justify-center opacity-20" aria-hidden="true">
          <div className="flex items-center space-x-1 md:space-x-0.5 sm:space-x-0.5">
            <div className="w-0 h-0 border-l-2 border-r-2 border-b-3 md:border-l-1 md:border-r-1 md:border-b-2 border-l-transparent border-r-transparent border-b-[#FCBD01]"></div>
            <div className="w-2 h-2 md:w-1.5 md:h-1.5 sm:w-1 sm:h-1 bg-[#FCBD01] transform rotate-45"></div>
            <div className="w-1.5 h-1.5 md:w-1 md:h-1 sm:w-0.5 sm:h-0.5 border border-[#FCBD01] rounded-full"></div>
          </div>
        </div>

        {/* Main sliding content */}
        <div
          className="flex items-center h-full overflow-hidden w-full"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          role="marquee"
          aria-live="polite"
          aria-label="Scrolling promotional offers"
        >
          <div
            ref={containerRef}
            className="flex items-center whitespace-nowrap"
            style={{
              width: "max-content",
              animationName: 'marquee',
              animationDuration: `${animationDuration}s`,
              animationTimingFunction: 'linear',
              animationIterationCount: 'infinite',
              animationPlayState: isPaused ? 'paused' : 'running'
            }}
          >
            {extendedPromoCodes.map((promo, index) => (
              <PromoItem
                key={promo.uniqueKey}
                promo={promo}
                onPromoClick={handlePromoClick}
                isLast={index === extendedPromoCodes.length - 1}
              />
            ))}
          </div>
        </div>

        {/* Gradient edges */}
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#FCBD01]/40 to-transparent" aria-hidden="true"></div>
        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#FCBD01]/40 to-transparent" aria-hidden="true"></div>

        <div className="absolute left-0 top-0 h-full w-16 bg-gradient-to-r from-[#076461] to-transparent pointer-events-none" aria-hidden="true"></div>
        <div className="absolute right-0 top-0 h-full w-16 bg-gradient-to-l from-[#076461] to-transparent pointer-events-none" aria-hidden="true"></div>
      </motion.section>

      {/* CSS Animation Keyframes */}
      <style >{`
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </>
  );
};

export default PromoRibbon;
